import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faArrowsUpDown,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import {
  SuiDialogConfig,
  DialogCloseOptions,
  DialogCloseReason,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogHeaderSlot,
  SuiDialogSimpleComponent,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { SuiButtonComponent } from '@sui/button';
import { SuiEmptyStateComponent } from '@sui/empty-state';
import { LoadingCardComponent } from 'src/app/shared/components/loading-card/loading-card.component';

import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';
import { FEED_MANUAL_ORDER_MAX_POSTS } from 'src/app/core/constants';
import { Feed, Post, Widget } from 'src/app/core/models/core.models';
import { FeedManualOrderService } from 'src/app/core/services/feed-manual-order.service';
import { selectFeedTimeShown } from 'src/app/core/state';
import { PostCardComponent } from '../../components/post-card/post-card.component';

export interface FeedManualOrderDialogData {
  /** Feed mode: the clipping feed whose saved order is arranged. */
  feed?: Feed;
  /** Widget mode: the content widget whose own order is arranged, over its feed of any type (SEN-5941). */
  widget?: Widget;
  feedId?: string;
}
interface DialogOutput {}

/**
 * Arrange the posts of a clipping feed by drag-and-drop (WAP-1888).
 *
 * Lists the feed under its saved manual order within the current period and
 * filters - the saved ids first, posts shared later newest first - and saves
 * the dragged sequence through `FeedManualOrderService`. Saved ids outside the
 * current period keep their place after the arranged ones, so arranging a
 * report period never drops a post from the order.
 *
 * Widget mode (`data.widget`): the same dialog over a content widget of any feed type - the feed
 * narrowed by the widget's attached filter dimensions, saved as the widget's own
 * `settings.manual_order`, so several widgets over one feed each keep their
 * sequence.
 */
@Component({
  selector: 'app-feed-manual-order-dialog',
  templateUrl: './feed-manual-order-dialog.component.html',
  styleUrl: './feed-manual-order-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiDialogContentSlot,
    SuiDialogFooterSlot,
    SuiButtonComponent,
    SuiEmptyStateComponent,
    LoadingCardComponent,
    FaIconComponent,
    CdkDropList,
    CdkDrag,
    forwardRef(() => PostCardComponent),
    TranslatePipe,
  ],
})
export class FeedManualOrderDialogComponent
  extends SimpleDialogBase<FeedManualOrderDialogData, DialogOutput>
  implements OnInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { size: 'medium' };
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  private readonly store = inject(Store);
  private readonly manualOrder = inject(FeedManualOrderService);

  readonly actionIcon: IconDefinition = faArrowsUpDown;
  readonly feed: Feed | undefined = this.dialogData.feed;
  readonly widget: Widget | undefined = this.dialogData.widget;
  readonly title: string = this.widget?.title ?? this.feed?.title ?? '';
  /** The attached filter dimensions the widget narrows the feed by, for the hint. */
  readonly dimensionNames: string = (this.widget?.dimensions_filter ?? [])
    .map((dimension) => dimension.title)
    .join(', ');
  private readonly feedId: string =
    this.dialogData.feedId ??
    this.widget?.settings?.datasource_object_id?.[0] ??
    this.feed?.id ??
    '';
  readonly timeShown = this.store.selectSignal(selectFeedTimeShown);
  readonly posts = signal<Post[]>([]);
  readonly loading = signal(true);
  /** Skeleton cards while the posts load: the widget's number of items, three for a feed. */
  readonly skeletonCount: number = this.widget
    ? Math.min(FeedManualOrderService.widgetItemsCount(this.widget), 5)
    : 3;
  readonly saving = signal(false);
  readonly dirty = signal(false);
  readonly capReached = computed(
    () => this.posts().length >= FEED_MANUAL_ORDER_MAX_POSTS,
  );
  private readonly isValid = computed(() => true);

  ngOnInit(): void {
    this.setupDirtyHandler(this.dirty, this.isValid);
    const load = this.widget
      ? this.manualOrder.loadOrderedForWidget(this.widget, this.feedId)
      : this.manualOrder.loadOrdered(this.feed);
    load
      .then((posts) => this.posts.set(posts))
      .finally(() => this.loading.set(false));
  }

  onDrop(event: CdkDragDrop<Post[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const reordered = [...this.posts()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.posts.set(reordered);
    this.dirty.set(true);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    const saved = await this.persist();
    this.saving.set(false);

    if (saved) {
      this.dirty.set(false);
      this.dirtyHandler.closeImmediately({ reason: DialogCloseReason.Save });
    }
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({ reason: DialogCloseReason.Cancel });
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    void this.persist();

    return { reason: DialogCloseReason.Save };
  }

  /** The one write of the dialog: the widget's own order in widget mode, the feed's order otherwise. */
  private persist(): Promise<boolean> {
    return this.widget
      ? this.manualOrder.saveForWidget(this.widget, this.orderedIds())
      : this.manualOrder.save(this.feed, this.orderedIds());
  }

  /** The arranged ids, then the saved ids outside the current period and filters. */
  private orderedIds(): string[] {
    const shown = this.posts().map((post) => post.id);
    const saved = this.widget
      ? (this.widget.settings?.manual_order ?? [])
      : (this.feed?.manual_order ?? []);
    const kept = saved.filter((id) => !shown.includes(id));

    return [...shown, ...kept];
  }
}
